<template>
  <MobileHeader :label="$t('nav.dashboard')" />
  <DashboardLayout>
    <template #main>
      <!-- Inspection history -->
      <DashboardChartPanel
        :showTitleOnMobile="true"
        :title="$t('shafts.inspection_history')"
      >
        <StackedChart
          v-if="inspectionHistory && !inspectionHistoryLoading"
          id="history"
          :height="200"
          :series="inspectionHistory"
        />
        <Loader
          v-if="inspectionHistoryLoading"
          class="chart-loader-history"
        />
      </DashboardChartPanel>

      <div class="chart-container">
        <div class="chart-selector typo-small-title hide-for-medium">
          <SwitchButton
            v-model="graph"
            :items="graphOptions"
            :modifiers="['soft', 'lighter']"
            name="graph"
          />
        </div>
        <!-- Issues by condition -->
        <DashboardChartPanel
          :class="{ 'show-for-medium': graph !== 'condition' }"
          :title="$t('operations.issues_by_condition')"
        >
          <template #panelHeaderActions>
            <DropdownLink
              v-if="earliestApprovedInspection && conditionParams"
              :items="[{ label: $t('general.csv'), path: conditionParams }]"
              :modifiers="['right-border', !conditionGraphData && 'hide']"
            />
            <DateFilter
              v-if="earliestApprovedInspection"
              id="condition"
              :class="!conditionGraphData && 'hide'"
              :earliestGraphDate="earliestApprovedInspection"
              :modifiers="['no-padding-right']"
              :showCustomDateRange="true"
              @update:model-value="filterGraph($event)"
            />
          </template>
          <DashboardChart
            v-if="conditionGraphData"
            chartStyle="condition"
            :series="conditionGraphData"
            @CSVparamUpdate="updateCSVPath"
          />
          <Loader
            v-else
            class="chart-loader-issue"
          />
        </DashboardChartPanel>
        <!-- Issues by risk / priority rating -->
        <DashboardChartPanel
          :class="{ 'show-for-medium': graph !== 'risk' }"
          :title="ratingPanelTitle"
        >
          <template #panelHeaderActions>
            <DropdownLink
              v-if="earliestApprovedInspection && ratingParams"
              :items="[{ label: $t('general.csv'), path: ratingParams }]"
              :modifiers="['right-border', !ratingGraphData && 'hide']"
            />
            <DateFilter
              v-if="earliestApprovedInspection"
              id="risk"
              :class="!ratingGraphData && 'hide'"
              :earliestGraphDate="earliestApprovedInspection"
              :modifiers="['no-padding-right']"
              :showCustomDateRange="true"
              @update:model-value="filterGraph($event)"
            />
          </template>
          <DashboardChart
            v-if="ratingGraphData"
            :chartStyle="graphType"
            :series="ratingGraphData"
            @CSVparamUpdate="updateCSVPath"
          />
          <Loader
            v-else
            class="chart-loader-issue"
          />
        </DashboardChartPanel>
      </div>
      <ReportsPanel
        :createPolicy="reportsStore.reportPolicies?.site_health_report_policy?.create"
        :fetchError="reportsStore.fetchReportsError"
        identifier="plant"
        :loading="reportsStore.loading"
        :reports="reportsStore.reports"
        @delete-report="onReportItem"
      />
    </template>
    <template #sidebar>
      <DashboardInspections
        inspectionPath="inspection"
        :inspectionsEndpoint="ENDPOINTS.inspections"
      />
      <DashboardIssues
        :highRiskRegionsEndpoint="ENDPOINTS.highRiskRegions"
        isPlant
        regionsPathName="regions"
      />
      <DashboardIssues
        v-if="hmeEnabled"
        :highRiskRegionsEndpoint="ENDPOINTS.highRiskHme"
        isHme
        regionsPathName="regionsTypeHmes"
      />
      <div class="section">
        <div class="section-heading typo-small-title">
          <UseSvg
            class="icon icon--small section-icon"
            name="dot_circle"
          />
          {{ $t('nav.monitoring_plans') }}
          <router-link
            class="section-link"
            :to="{ name: 'plans', params: { scope: 'plans' } }"
          >
            {{ showPlansLabel }}
            <UseSvg
              class="icon icon--small"
              name="arrow_right_16"
            />
          </router-link>
        </div>
        <div
          v-if="monitoringPlans"
          class="section-content"
        >
          <div
            v-if="monitoringPlans.completed_plan"
            class="section-sub-title typo-smallish"
          >
            {{ $t('operations.recently_conducted') }}
          </div>
          <PlanCard
            v-if="monitoringPlans.completed_plan"
            :plan="monitoringPlans.completed_plan"
          />
          <div
            v-if="monitoringPlans?.plans?.length > 0"
            :class="{
              'section-sub-title typo-smallish': true,
              'section-sub-title--margin-top': monitoringPlans.completed_plan,
            }"
          >
            {{ $t('operations.upcoming') }}
          </div>
          <PlanCard
            v-for="plan in monitoringPlans.plans"
            :key="plan.id"
            :plan="plan"
          />
          <div
            v-if="!monitoringPlans.completed_plan && monitoringPlans?.plans?.length === 0"
            class="section-empty typo-small-title"
          >
            <div class="section-empty-title">
              {{ $t('operations.monitoring_plans_empty_title') }}
            </div>
            <div class="section-empty-message">
              {{ $t('operations.monitoring_plans_empty_message') }}
            </div>
          </div>
        </div>
        <Loader
          v-if="!monitoringPlans"
          class="section-loader"
        />
      </div>
    </template>
  </DashboardLayout>
</template>

<script>
export default { name: 'OperationDashboard' };
</script>

<script setup>
import { computed, onMounted, ref, watch, inject } from 'vue';
import { useChannel } from '@/functions/action_cable';

import I18n from 'i18n-js';
import useAjax from '@/functions/use_ajax';
import useOrganisationProfile from '@/functions/use_organisation_profile';

// Stores
import { useReportsStore } from '@/stores/reports/reportsStore';

// Components
import DashboardChart from '@/components/charts/dashboard_chart.vue';
import DashboardChartPanel from '@/components/dashboard/dashboard_chart_panel.vue';
import DashboardInspections from '@/components/dashboard/dashboard_inspections.vue';
import DashboardIssues from '@/components/dashboard/dashboard_issues.vue';
import DashboardLayout from '@/components/dashboard/dashboard_layout.vue';
import DateFilter from '@/components/form/date_filter.vue';
import DropdownLink from '@/components/generic/download_link.vue';
import Loader from '@/components/generic/loader.vue';
import MobileHeader from '@/components/generic/mobile_header.vue';
import PlanCard from '@/components/monitoring_plans/plan_card.vue';
import ReportsPanel from '@/components/dashboard/reports/reports_panel.vue';
import StackedChart from '@/components/charts/stacked_chart.vue';
import SwitchButton from '@/components/form/switch_button.vue';
import UseSvg from '@/components/generic/use_svg.vue';

const props = defineProps({
  operationId: { type: String, required: true },
  organisationId: { type: String, required: true },
});

const ajax = useAjax();
const reportsStore = useReportsStore();
const currentOperation = inject('currentOperation');

useChannel(
  {
    channel: 'Plants::SiteHealthReportChannel',
    operation_id: props.operationId,
  },
  {
    received() {
      reportsStore.fetchReports(ENDPOINTS.value.siteHealthReports);
    },
  }
);

const chartFilterDateRange = ref({});
const conditionGraphData = ref(null);
const conditionParams = ref(null);
const earliestApprovedInspection = ref('');
const graph = ref('condition');
const inspectionHistory = ref(null);
const inspectionHistoryLoading = ref(false);
const monitoringPlans = ref(null);
const ratingChartFilterDateRange = ref({});
const ratingGraphData = ref(null);
const ratingParams = ref(null);

const { profileLoaded, showRisk } = useOrganisationProfile();

const graphType = computed(() => {
  if (!profileLoaded.value) return null;

  return showRisk.value ? 'risk' : 'priority';
});

// Derived from graphType rather than showRisk so the panel never briefly shows
// the wrong heading while the organisation profile is still loading.
const ratingPanelTitle = computed(() => {
  if (!graphType.value) return '';

  return graphType.value === 'risk'
    ? I18n.t('operations.issues_by_risk')
    : I18n.t('operations.issues_by_priority');
});

const showPlansLabel = computed(() => {
  if (!monitoringPlans.value) return '';

  return monitoringPlans.value.plans?.length > 0 ? I18n.t('general.see_all') : I18n.t('general.view');
});

const graphOptions = computed(() => [
  { label: I18n.t('operations.condition'), value: 'condition' },
  {
    label: graphType.value === 'priority' ? I18n.t('operations.priority') : I18n.t('operations.risk'),
    value: 'risk',
  },
]);

const hmeEnabled = computed(
  () => currentOperation.value?.policy?.show_heavy_mobile_equipment
);

const BASE_URL = computed(() => `/organisations/${props.organisationId}/operations/${props.operationId}/plants`);

const ENDPOINTS = computed(() => {
  return {
    csv: `/internal${BASE_URL.value}/graph/download?type=`,
    dashboard: `${BASE_URL.value}/dashboard`,
    graph: `${BASE_URL.value}/graph`,
    highRiskRegions: `${BASE_URL.value}/dashboard/high_risk_regions`,
    inspections: `${BASE_URL.value}/dashboard/inspections`,
    monitoringPlans: `${BASE_URL.value}/dashboard/monitoring_plans`,
    siteHealthReportPolicies: `${BASE_URL.value}/dashboard/site_health_report_policies`,
    siteHealthReports: `${BASE_URL.value}/site_health_reports`,
    highRiskHme: `${BASE_URL.value}/dashboard/high_risk_heavy_mobile_equipments`,
  };
});

onMounted(load);

watch(() => props.operationId, load);

// The rating graph is loaded separately because it cannot be requested until the
// organisation profile is known.
watch([graphType, () => props.operationId], loadRatingGraphData, { immediate: true });

function filterGraph({ id, from, to }) {
  if (id === 'condition') {
    chartFilterDateRange.value = { from, to };
    loadGraphData('condition', 'apex', conditionGraphData, chartFilterDateRange.value);
  } else {
    ratingChartFilterDateRange.value = { from, to };
    loadRatingGraphData();
  }
}

function loadRatingGraphData() {
  if (!graphType.value) return;

  loadGraphData(graphType.value, 'apex', ratingGraphData, ratingChartFilterDateRange.value);
}

function load() {
  loadOperation();

  loadGraphData('condition', 'apex', conditionGraphData, chartFilterDateRange.value);

  reportsStore.fetchReports(ENDPOINTS.value.siteHealthReports);
  reportsStore.fetchReportPolicies(ENDPOINTS.value.siteHealthReportPolicies);
  loadInspectionHistoryData();
  loadMonitoringPlans();
}

async function loadOperation() {
  let { ok, data } = await ajax(ENDPOINTS.value.dashboard);

  if (ok) {
    earliestApprovedInspection.value = data.earliest_approved_inspection;
  }
}

async function loadGraphData(type, graph, ref, filterParams) {
  ref.value = null;
  let { ok, data } = await ajax(ENDPOINTS.value.graph, {
    query: {
      type,
      graph,
      start_date: filterParams?.from,
      end_date: filterParams?.to,
    },
  });

  if (ok) {
    ref.value = data;
  }
}

async function loadMonitoringPlans() {
  let { ok, data } = await ajax(ENDPOINTS.value.monitoringPlans);

  if (ok) {
    monitoringPlans.value = data;
  }
}

async function loadInspectionHistoryData() {
  inspectionHistoryLoading.value = true;

  let { ok, data } = await ajax(ENDPOINTS.value.graph, {
    query: {
      type: 'detailed_inspection_history',
    },
  });

  if (ok) {
    inspectionHistory.value = data;
    inspectionHistoryLoading.value = false;
  }
}

async function onReportItem(id) {
  reportsStore.deleteReport(id, `${ENDPOINTS.value.siteHealthReports}/${id}`);
}

function updateCSVPath(data, type) {
  let endpoint = ENDPOINTS.value.csv;

  if (type === 'condition') {
    let params = data.map((item) => `&conditions[]=${item}`);
    return conditionParams.value = buildEndPoint(endpoint, type, params, startAndEndParam(chartFilterDateRange.value));
  }
  if (type === 'risk') {
    let params = data.map((item) => `&risk_rating_levels[]=${item}`);
    return ratingParams.value = buildEndPoint(endpoint, type, params, startAndEndParam(ratingChartFilterDateRange.value));
  }
  if (type === 'priority') {
    let params = data.map((item) => `&priorities[]=${item}`);
    return ratingParams.value = buildEndPoint(endpoint, type, params, startAndEndParam(ratingChartFilterDateRange.value));
  }
}

function startAndEndParam(rangeType) {
  return Object.keys(rangeType).length !== 0 ? `&start_date="${rangeType.from}&end_date="${rangeType.to}"` : '';
}

function buildEndPoint(endpoint, type, params, startEnd) {
  return `${endpoint}${type}${params.join('')}${startEnd}`;
}
</script>
<style scoped lang="scss">
@import '@/stylesheets/global';

.sections {
  @include breakpoint(medium) {
    width: rem(337);
    grid-column: 2;
    grid-row: 1 / span 3;
  }
}

.section {
  background-color: map-get($background-color, dark);
  border-radius: map-get($border-radius, mega);
  margin-bottom: bu(1.5);
  overflow: hidden;
}

.section-heading {
  display: flex;
  align-items: center;
  width: 100%;
  padding: bu(1.5);
  border-bottom: 1px solid map-get($background-color, default);
  font-weight: 500;
}

.section-icon {
  margin-right: bu(1.5);
  color: map-get($foreground-color, dark);
}
.section-link {
  margin-left: auto;
  display: flex;
  flex-shrink: 0;
  align-items: center;
  color: map-get($foreground-color, dark);
  font-weight: 300;
}

.section-content {
  padding: bu(1);

  &--no-padding {
    padding: 0;
  }
}

.section-sub-title {
  color: map-get($foreground-color, dark);
  margin-left: bu(1.5);
  margin-bottom: bu(0.75);

  &--margin-top {
    margin-top: bu(0.75);
  }
}

.section-empty {
  padding: bu(2);
  color: map-get($foreground-color, dark);
}

.section-empty-title {
  margin-bottom: bu(1);
  font-weight: 500;
}
</style>
